import { clamp, isNil, map, mapValues, max, findIndex, compact } from 'lodash/fp';
import { DateTime } from 'luxon';

export const TIMESTAMP_FORMAT = 'yyyy-LL-dd HH:mm:ss';
export const DATE_FORMAT = 'yyyy-LL-dd';

const MIN_COLUMN_WIDTH = 200;
const MAX_COLUMN_WIDTH = 300;
const CELL_CHAR_WIDTH = 8;
const HEADER_CHAR_WIDTH = 10;

const gatherOperationParams = ({ _type, uid, ...params }) => ({ _type, uid, params });

export const transformView = (view) => {
    if ('transformationsByUid' in view) {
        view.transformationsByUid = mapValues(gatherOperationParams, view.transformationsByUid);
    }

    if ('spaceRef' in view) {
        view.spaceName = view.spaceRef.name;
        view.spaceUid = view.spaceRef.uid;
        view.spaceSlug = view.spaceRef.slug;
    }

    // Make the slug durable and unique - this is a stop gap measure until we can fully
    // remove our reliance on slugs.
    view.slug = view.uid;

    return view;
};

export const transformDataset = (data, entityId = null) => {
    const { columns, rowCount } = data;

    const columnDescriptors = data.schema?.columns ?? data.columnDescriptors;
    const columnsCount = columns.length;

    const transformedData = {
        columnDescriptors,
        rowCount,
        columns: Array(columnsCount),
        columnWidths: []
    };

    if (entityId) {
        transformedData.entityId = entityId;
    }

    const makeCalcCellWidth = (i) => (cell) =>
        isNil(cell)
            ? 0
            : columnDescriptors[i].typeFamily === 'TIMESTAMP'
              ? cell.toFormat(TIMESTAMP_FORMAT).length
              : cell.toString().length;

    for (let i = 0; i < columnsCount; i++) {
        const column = columns[i];
        const { name, typeFamily, type } = columnDescriptors[i];

        switch (typeFamily) {
            case 'DATE':
            case 'TIMESTAMP':
                transformedData.columns[i] = column.map((value) =>
                    !isNil(value) ? DateTime.fromISO(value, { zone: 'UTC' }) : value
                );

                break;
            case 'DECIMAL':
            case 'DOUBLE':
                transformedData.columns[i] = column.map((value) => {
                    const numberedValue = Number(value);

                    return !isNil(value) && numberedValue % 1 === 0
                        ? numberedValue.toFixed(1)
                        : value;
                });

                break;
            case 'JSON':
                transformedData.columns[i] = column.map((value) =>
                    type === 'ARRAY' ? JSON.stringify(value.array) : value
                );

                break;
            default:
                transformedData.columns[i] = column;
        }

        const calcCellWidth = makeCalcCellWidth(i);
        const cellWidths = transformedData.columns[i].slice(0, 100).map(calcCellWidth);

        const headerWidth = name.length;

        transformedData.columnWidths[i] = clamp(
            MIN_COLUMN_WIDTH,
            MAX_COLUMN_WIDTH,
            max([CELL_CHAR_WIDTH * max(cellWidths), HEADER_CHAR_WIDTH * headerWidth])
        );
    }

    return transformedData;
};

export const transformSpace = ({ data }) => ({
    _type: 'Space',
    ...data,
    viewsByUid: mapValues(transformView, data.viewsByUid),
    schemasByUid: data.viewUids.reduce((schemas, id) => {
        schemas[id] = data.viewsByUid[id].schema;
        return schemas;
    }, {})
});

export const applyCustomColumnWidths =
    (updatedColumnWidths = {}) =>
    (dataset) => {
        const columnWidths = [...dataset.columnWidths];
        const columnWidthsSetByUser = new Array(columnWidths.length).fill(false);
        for (const name in updatedColumnWidths) {
            const i = findIndex({ name }, dataset.columnDescriptors);

            if (i !== -1) {
                columnWidths[i] = updatedColumnWidths[name];
                columnWidthsSetByUser[i] = true;
            }
        }

        return {
            ...dataset,
            columnWidths,
            columnWidthsSetByUser
        };
    };

export const sieve = (array, indexes) => compact(map((i) => array[i], indexes));

export const applySchemaFilter = (schema, columnNames) =>
    isNil(columnNames) || isNil(schema)
        ? schema
        : schema.filter(({ name }) => columnNames.includes(name));

export const applyColumnFilter = (columnNames) => (dataset) => {
    if (isNil(columnNames)) {
        return dataset;
    }

    const indexes = dataset.columnDescriptors.flatMap(({ name }, index) =>
        columnNames.includes(name) ? [index] : []
    );

    return {
        ...dataset,
        columns: sieve(dataset.columns, indexes),
        columnDescriptors: sieve(dataset.columnDescriptors, indexes),
        columnWidths: sieve(dataset.columnWidths, indexes)
    };
};

export const applyEntityId = (entityId) => (dataset) => ({
    ...dataset,
    entityId
});
